import { useState, useCallback, useEffect } from 'react'
import type { AssetType, FoundryPreset } from '../types'
import { TagFilter } from './TagFilter'
import { TAG_CATEGORIES, smartMergeTags } from '../lib/tagVocabulary'

interface Props {
  generating: boolean
  onGenerate: (req: {
    type: AssetType
    name: string
    prompt: string
    tags: string[]
    aspectRatio?: string
    sampleCount?: number
  }) => Promise<unknown>
}

type CreationMode = 'choose' | 'preset' | 'custom'

function expandTemplate(template: string, tags: string[]): string {
  const tagSet = new Set(tags)

  const genderTag = ['male', 'female', 'random-gender'].find((t) => tagSet.has(t))
  const genderWord = genderTag === 'male' ? 'man' : genderTag === 'female' ? 'woman' : 'person'

  const ageTag = ['20s', '30s', '40s', '50s', '60s+'].find((t) => tagSet.has(t))
  const attireTags = ['casual', 'chef-whites', 'formal', 'athletic'].filter((t) => tagSet.has(t))
  const settingTags = TAG_CATEGORIES.find((c) => c.id === 'setting')?.values.filter((v) => tagSet.has(v.id)) || []
  const timeTags = TAG_CATEGORIES.find((c) => c.id === 'time-of-day')?.values.filter((v) => tagSet.has(v.id)) || []

  let result = template
  result = result.replace('{gender}', genderWord)
  result = result.replace('{age}', ageTag || '30s')
  result = result.replace('{attire}', attireTags.length ? attireTags.join(' and ') : 'casual clothing')
  result = result.replace('{setting}', settingTags.length ? settingTags[0].label.toLowerCase() : 'home kitchen')
  result = result.replace('{time}', timeTags.length ? timeTags[0].label.toLowerCase() : 'evening')

  return result
}

export function AssetCreator({ generating, onGenerate }: Props) {
  const [type, setType] = useState<AssetType>('character')
  const [mode, setMode] = useState<CreationMode>('choose')
  const [name, setName] = useState('')
  const [prompt, setPrompt] = useState('')
  const [tags, setTags] = useState<string[]>([])
  const [aspectRatio, setAspectRatio] = useState('1:1')
  const [sampleCount, setSampleCount] = useState(1)
  const [activePromptPattern, setActivePromptPattern] = useState<string | null>(null)
  const [enhancing, setEnhancing] = useState(false)
  const [originalPrompt, setOriginalPrompt] = useState<string | null>(null)

  const [presets, setPresets] = useState<FoundryPreset[]>([])
  const [loadingPresets, setLoadingPresets] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState<FoundryPreset | null>(null)

  const [savingPreset, setSavingPreset] = useState(false)
  const [savePresetName, setSavePresetName] = useState('')
  const [showSaveDialog, setShowSaveDialog] = useState(false)

  const fetchPresets = useCallback(async (assetType: AssetType) => {
    setLoadingPresets(true)
    try {
      const res = await fetch(`/api/foundry/presets?type=${assetType}`, { cache: 'no-store' })
      if (res.ok) {
        const data = await res.json() as FoundryPreset[]
        setPresets(data)
      }
    } catch { /* silent */ } finally {
      setLoadingPresets(false)
    }
  }, [])

  useEffect(() => { fetchPresets(type) }, [type, fetchPresets])

  const systemPresets = presets.filter((p) => p.is_system)
  const userPresets = presets.filter((p) => !p.is_system)

  const resetEditor = () => {
    setName('')
    setPrompt('')
    setTags([])
    setActivePromptPattern(null)
    setOriginalPrompt(null)
    setSelectedPreset(null)
  }

  const handleTypeSwitch = (t: AssetType) => {
    setType(t)
    setMode('choose')
    resetEditor()
  }

  const handleSelectPreset = (preset: FoundryPreset) => {
    setSelectedPreset(preset)
    setTags([...preset.tags])
    if (preset.prompt_pattern) {
      const expanded = expandTemplate(preset.prompt_pattern, preset.tags)
      setPrompt(expanded)
      setActivePromptPattern(preset.prompt_pattern)
    } else {
      setPrompt('')
      setActivePromptPattern(null)
    }
    setOriginalPrompt(null)
  }

  const handleTagChange = useCallback((tag: string) => {
    setTags((prev) => {
      const next = prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
      setActivePromptPattern((currentPattern) => {
        if (currentPattern) {
          setPrompt(expandTemplate(currentPattern, next))
        }
        return currentPattern
      })
      return next
    })
  }, [])

  const handleSingleSelect = useCallback((newTag: string, removeTags: string[]) => {
    setTags((prev) => {
      const next = [...prev.filter((t) => !removeTags.includes(t)), newTag]
      setActivePromptPattern((currentPattern) => {
        if (currentPattern) {
          setPrompt(expandTemplate(currentPattern, next))
        }
        return currentPattern
      })
      return next
    })
  }, [])

  const handlePromptEdit = (value: string) => {
    setPrompt(value)
    if (activePromptPattern) setActivePromptPattern(null)
  }

  const handleEnhance = async () => {
    if (!prompt.trim()) return
    setEnhancing(true)
    setOriginalPrompt(prompt)
    try {
      const res = await fetch('/api/foundry/enhance-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, type, tags }),
      })
      if (!res.ok) throw new Error('Enhancement failed')
      const data = await res.json() as { enhanced: string }
      setPrompt(data.enhanced)
      setActivePromptPattern(null)
    } catch {
      setOriginalPrompt(null)
    } finally {
      setEnhancing(false)
    }
  }

  const handleRevertPrompt = () => {
    if (originalPrompt) {
      setPrompt(originalPrompt)
      setOriginalPrompt(null)
    }
  }

  const handleSavePreset = async () => {
    if (!savePresetName.trim() || !tags.length) return
    setSavingPreset(true)
    try {
      const res = await fetch('/api/foundry/presets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          name: savePresetName.trim(),
          tags,
          prompt_pattern: activePromptPattern || prompt || null,
        }),
      })
      if (res.ok) {
        setShowSaveDialog(false)
        setSavePresetName('')
        await fetchPresets(type)
      }
    } catch { /* silent */ } finally {
      setSavingPreset(false)
    }
  }

  const handleDeletePreset = async (presetId: string) => {
    try {
      const res = await fetch(`/api/foundry/presets/${presetId}`, { method: 'DELETE' })
      if (res.ok) {
        await fetchPresets(type)
        if (selectedPreset?.id === presetId) {
          setSelectedPreset(null)
        }
      }
    } catch { /* silent */ }
  }

  const handleGenerate = async () => {
    if (!name.trim() || !prompt.trim()) return
    await onGenerate({ type, name: name.trim(), prompt: prompt.trim(), tags, aspectRatio, sampleCount })
  }

  const canGenerate = name.trim() && prompt.trim() && !generating

  return (
    <div className="flex-1 overflow-auto p-5">
      <div className="max-w-3xl mx-auto space-y-5">

        {/* Type selector */}
        <div>
          <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1.5">Asset Type</label>
          <div className="flex rounded-md border border-forge-slate overflow-hidden w-fit">
            {(['character', 'scene'] as const).map((t) => (
              <button
                key={t}
                onClick={() => handleTypeSwitch(t)}
                className={`text-[12px] font-semibold px-5 py-2 transition-colors ${
                  type === t
                    ? 'bg-forge-ember text-white'
                    : 'text-forge-ash hover:text-forge-primary hover:bg-forge-basalt'
                }`}
              >
                {t === 'character' ? 'Character' : 'Scene'}
              </button>
            ))}
          </div>
        </div>

        {/* Mode chooser — prominent at the top */}
        {mode === 'choose' && (
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setMode('preset')}
              className="group p-5 rounded-lg border-2 border-forge-slate hover:border-forge-ember/60 bg-forge-basalt/30 hover:bg-forge-ember/5 transition-all text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-forge-ember/15 flex items-center justify-center">
                  <svg className="w-5 h-5 text-forge-ember" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
                <span className="text-[14px] font-bold text-forge-primary group-hover:text-forge-ember transition-colors">Create from Preset</span>
              </div>
              <p className="text-[11px] text-forge-ash leading-relaxed">
                Start from a pre-configured tag and prompt combination. Adjust attributes before generating.
              </p>
            </button>

            <button
              onClick={() => setMode('custom')}
              className="group p-5 rounded-lg border-2 border-forge-slate hover:border-forge-ember/60 bg-forge-basalt/30 hover:bg-forge-ember/5 transition-all text-left"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-forge-ember/15 flex items-center justify-center">
                  <svg className="w-5 h-5 text-forge-ember" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </div>
                <span className="text-[14px] font-bold text-forge-primary group-hover:text-forge-ember transition-colors">Create Custom</span>
              </div>
              <p className="text-[11px] text-forge-ash leading-relaxed">
                Build from scratch — select attributes, write your own prompt, and generate.
              </p>
            </button>
          </div>
        )}

        {/* Preset browser */}
        {mode === 'preset' && !selectedPreset && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-[13px] font-bold text-forge-primary">Choose a Preset</h3>
              <button onClick={() => { setMode('choose'); resetEditor() }} className="text-[11px] text-forge-ash hover:text-forge-ember transition-colors">
                ← Back
              </button>
            </div>

            {loadingPresets ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-forge-ember border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* System presets */}
                {systemPresets.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold mb-2">System Presets</p>
                    <div className="grid grid-cols-2 gap-2">
                      {systemPresets.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => handleSelectPreset(p)}
                          className="group p-3 rounded-lg border border-forge-slate hover:border-forge-ember/50 bg-forge-basalt/20 hover:bg-forge-ember/5 transition-all text-left"
                        >
                          <div className="flex items-center gap-2 mb-1.5">
                            <svg className="w-3.5 h-3.5 text-forge-cool-smoke" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            <span className="text-[12px] font-semibold text-forge-primary group-hover:text-forge-ember transition-colors">{p.name}</span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {p.tags.slice(0, 5).map((tag) => (
                              <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-forge-slate/60 text-forge-ash">{tag}</span>
                            ))}
                            {p.tags.length > 5 && (
                              <span className="text-[9px] text-forge-cool-smoke">+{p.tags.length - 5}</span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* User presets */}
                {userPresets.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold mb-2">My Presets</p>
                    <div className="grid grid-cols-2 gap-2">
                      {userPresets.map((p) => (
                        <div key={p.id} className="group relative">
                          <button
                            onClick={() => handleSelectPreset(p)}
                            className="w-full p-3 rounded-lg border border-forge-slate hover:border-forge-ember/50 bg-forge-basalt/20 hover:bg-forge-ember/5 transition-all text-left"
                          >
                            <div className="flex items-center gap-2 mb-1.5">
                              <svg className="w-3.5 h-3.5 text-forge-ember/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                              </svg>
                              <span className="text-[12px] font-semibold text-forge-primary group-hover:text-forge-ember transition-colors">{p.name}</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {p.tags.slice(0, 5).map((tag) => (
                                <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-forge-slate/60 text-forge-ash">{tag}</span>
                              ))}
                              {p.tags.length > 5 && (
                                <span className="text-[9px] text-forge-cool-smoke">+{p.tags.length - 5}</span>
                              )}
                            </div>
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDeletePreset(p.id) }}
                            className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity text-forge-cool-smoke hover:text-red-400 p-1"
                            title="Delete preset"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {presets.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-[12px] text-forge-cool-smoke">No presets found for this type.</p>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* Editor (visible once a preset is selected or custom mode) */}
        {(mode === 'custom' || (mode === 'preset' && selectedPreset)) && (
          <>
            {/* Header with back and preset info */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <button onClick={() => { setMode('choose'); resetEditor() }} className="text-[11px] text-forge-ash hover:text-forge-ember transition-colors">
                  ← Back
                </button>
                {selectedPreset && (
                  <span className="text-[11px] text-forge-cool-smoke">
                    Based on: <span className="text-forge-ember font-semibold">{selectedPreset.name}</span>
                    {selectedPreset.is_system && (
                      <svg className="w-3 h-3 text-forge-cool-smoke inline ml-1 -mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                    )}
                  </span>
                )}
              </div>
              {tags.length > 0 && (
                <button
                  onClick={() => { setSavePresetName(''); setShowSaveDialog(true) }}
                  className="text-[10px] font-semibold px-3 py-1 rounded-md text-forge-gold hover:bg-forge-gold/10 transition-all"
                >
                  Save as Preset
                </button>
              )}
            </div>

            {/* Name */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={type === 'character' ? 'e.g., Baker Dan' : 'e.g., Warm Evening Kitchen'}
                className="w-full bg-forge-obsidian border border-forge-slate rounded-md px-3 py-2 text-sm text-forge-primary placeholder:text-forge-cool-smoke focus:border-forge-ember focus:outline-none"
              />
            </div>

            {/* Tags */}
            <div>
              <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1.5">Attributes</label>
              <TagFilter type={type} selectedTags={tags} onToggleTag={handleTagChange} onSingleSelect={handleSingleSelect} />
              {tags.length > 0 && (
                <p className="text-[10px] text-forge-ember/70 mt-1.5">
                  Selected attributes are always injected into the prompt — Imagen cannot ignore them.
                </p>
              )}
            </div>

            {/* Prompt */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold">
                  Generation Prompt
                </label>
                <button
                  onClick={handleEnhance}
                  disabled={!prompt.trim() || enhancing}
                  className={`text-[10px] font-semibold px-3 py-1 rounded-md transition-all ${
                    !prompt.trim() || enhancing
                      ? 'text-forge-cool-smoke cursor-not-allowed'
                      : 'text-forge-gold hover:bg-forge-gold/10'
                  }`}
                >
                  {enhancing ? (
                    <span className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 border-2 border-forge-gold border-t-transparent rounded-full animate-spin" />
                      Enhancing...
                    </span>
                  ) : (
                    '✦ Enhance with AI'
                  )}
                </button>
              </div>
              <textarea
                value={prompt}
                onChange={(e) => handlePromptEdit(e.target.value)}
                rows={6}
                placeholder="Describe the character or scene in detail..."
                className="w-full bg-forge-obsidian border border-forge-slate rounded-md px-3 py-2 text-sm text-forge-primary placeholder:text-forge-cool-smoke focus:border-forge-ember focus:outline-none resize-y"
              />
              {originalPrompt && (
                <div className="mt-1.5 p-2 bg-forge-basalt/50 border border-forge-slate/50 rounded-md">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-forge-cool-smoke uppercase tracking-wider">Original prompt</span>
                    <button
                      onClick={handleRevertPrompt}
                      className="text-[10px] text-forge-ember hover:underline"
                    >
                      Revert
                    </button>
                  </div>
                  <p className="text-[11px] text-forge-ash mt-1 line-clamp-3">{originalPrompt}</p>
                </div>
              )}
              {activePromptPattern && (
                <p className="text-[10px] text-forge-cool-smoke mt-1">
                  Preset prompt active — changing tags will auto-update. Edit manually to go freeform.
                </p>
              )}
            </div>

            {/* Options row */}
            <div className="flex items-end gap-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Aspect Ratio</label>
                <select
                  value={aspectRatio}
                  onChange={(e) => setAspectRatio(e.target.value)}
                  className="bg-forge-obsidian border border-forge-slate rounded-md px-3 py-2 text-sm text-forge-primary focus:border-forge-ember focus:outline-none"
                >
                  <option value="1:1">1:1 (Square)</option>
                  <option value="4:3">4:3 (Landscape)</option>
                  <option value="3:4">3:4 (Portrait)</option>
                  <option value="16:9">16:9 (Wide)</option>
                  <option value="9:16">9:16 (Tall)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-forge-ash font-semibold block mb-1">Variations</label>
                <select
                  value={sampleCount}
                  onChange={(e) => setSampleCount(parseInt(e.target.value, 10))}
                  className="bg-forge-obsidian border border-forge-slate rounded-md px-3 py-2 text-sm text-forge-primary focus:border-forge-ember focus:outline-none"
                >
                  <option value={1}>1 image</option>
                  <option value={2}>2 variations</option>
                  <option value={3}>3 variations</option>
                  <option value={4}>4 variations</option>
                </select>
              </div>
              <div className="flex-1" />
              <button
                onClick={handleGenerate}
                disabled={!canGenerate}
                className={`text-[12px] font-semibold rounded-md px-6 py-2.5 transition-all ${
                  canGenerate
                    ? 'bg-forge-ember text-white hover:bg-forge-ember/80'
                    : 'bg-forge-slate text-forge-cool-smoke cursor-not-allowed'
                }`}
              >
                {generating ? (
                  <span className="flex items-center gap-2">
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </span>
                ) : (
                  `Generate ${type === 'character' ? 'Character' : 'Scene'}`
                )}
              </button>
            </div>
          </>
        )}

        {/* Save as Preset dialog */}
        {showSaveDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
            <div className="bg-forge-obsidian border border-forge-slate rounded-xl p-5 w-full max-w-sm shadow-2xl">
              <h3 className="text-[14px] font-bold text-forge-primary mb-3">Save as Preset</h3>
              <p className="text-[11px] text-forge-ash mb-3">
                Save your current tag combination{activePromptPattern ? ' and prompt pattern' : ''} as a reusable preset.
              </p>
              <input
                type="text"
                value={savePresetName}
                onChange={(e) => setSavePresetName(e.target.value)}
                placeholder="Preset name..."
                autoFocus
                className="w-full bg-forge-basalt border border-forge-slate rounded-md px-3 py-2 text-sm text-forge-primary placeholder:text-forge-cool-smoke focus:border-forge-ember focus:outline-none mb-3"
                onKeyDown={(e) => { if (e.key === 'Enter') handleSavePreset() }}
              />
              <div className="flex flex-wrap gap-1 mb-4">
                {tags.map((tag) => (
                  <span key={tag} className="text-[9px] px-1.5 py-0.5 rounded bg-forge-ember/15 text-forge-ember">{tag}</span>
                ))}
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="text-[11px] px-4 py-1.5 rounded-md border border-forge-slate text-forge-ash hover:text-forge-primary transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSavePreset}
                  disabled={!savePresetName.trim() || savingPreset}
                  className={`text-[11px] font-semibold px-4 py-1.5 rounded-md transition-all ${
                    savePresetName.trim() && !savingPreset
                      ? 'bg-forge-ember text-white hover:bg-forge-ember/80'
                      : 'bg-forge-slate text-forge-cool-smoke cursor-not-allowed'
                  }`}
                >
                  {savingPreset ? 'Saving...' : 'Save Preset'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
